import React, { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { AlertTriangle, Check, X, ArrowUpRight, Wrench } from "lucide-react";
import { useData } from "@/context/DataContext";
import Seo from "@/components/site/Seo";
import { Reveal } from "@/components/site/Reveal";
import { GuideCard, GuideCta } from "@/components/site/GuideBits";
import { resolveImg } from "@/lib/api";
import { track } from "@/lib/track";

export default function GuidePost() {
  const { slug } = useParams();
  const { guides, guideCategories } = useData();
  const g = guides.find((x) => x.slug === slug);

  useEffect(() => { if (g) track("guide_view", g.titolo, `/guide/${slug}`); }, [g, slug]);

  if (!g) {
    return (
      <div className="min-h-[60vh] bg-av-black flex flex-col items-center justify-center px-6 text-center">
        <h1 className="font-display text-4xl text-av-white">Guida non trovata</h1>
        <Link to="/guide" className="mt-6 text-av-lime">← Torna alle guide</Link>
      </div>
    );
  }

  const cat = guideCategories.find((c) => c.slug === g.categoria);
  const related = guides.filter((x) => (g.guide_correlate || []).includes(x.slug));

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      { "@type": "BreadcrumbList", itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://www.giardinianimaverde.it/" },
        { "@type": "ListItem", position: 2, name: "Guide", item: "https://www.giardinianimaverde.it/guide" },
        { "@type": "ListItem", position: 3, name: g.titolo, item: `https://www.giardinianimaverde.it/guide/${g.slug}` },
      ]},
      { "@type": "Article", headline: g.titolo, description: g.risposta_breve,
        datePublished: g.data_pubblicazione, dateModified: g.data_aggiornamento,
        author: { "@type": "Organization", name: "Animaverde" }, publisher: { "@type": "Organization", name: "Animaverde" } },
      { "@type": "FAQPage", mainEntity: [{ "@type": "Question", name: g.domanda || g.titolo, acceptedAnswer: { "@type": "Answer", text: g.risposta_breve } }] },
    ],
  };

  const Block = ({ title, items, icon }) => items && items.length > 0 ? (
    <div className="mt-8">
      <h2 className="font-display text-2xl text-av-white mb-4">{title}</h2>
      <ul className="space-y-2.5">
        {items.map((it, i) => (
          <li key={i} className="flex items-start gap-3 text-av-white/85">{icon}<span>{it}</span></li>
        ))}
      </ul>
    </div>
  ) : null;

  return (
    <>
      <Seo title={g.meta_title || g.titolo} description={g.meta_description || g.risposta_breve} path={`/guide/${g.slug}`} schema={schema} />
      <article className="bg-av-black">
        <div className="relative h-[42vh] min-h-[300px] overflow-hidden">
          <img src={resolveImg(g.immagine)} alt={g.titolo} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-av-black via-av-black/50 to-av-black/40" />
          <div className="absolute inset-0 flex items-end">
            <div className="mx-auto max-w-3xl w-full px-5 sm:px-8 pb-10">
              <nav className="text-xs text-av-gray mb-3">
                <Link to="/" className="hover:text-av-lime">Home</Link> / <Link to="/guide" className="hover:text-av-lime">Guide</Link> / {cat && <Link to={`/guide/categoria/${cat.slug}`} className="hover:text-av-lime">{cat.nome}</Link>}
              </nav>
              <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-av-white text-balance">{g.titolo}</h1>
            </div>
          </div>
        </div>

        <div className="mx-auto max-w-3xl px-5 sm:px-8 py-14">
          <Reveal>
            {/* Short answer */}
            <div className="rounded-2xl border border-av-lime/30 bg-av-green-dark/40 p-6" style={{ backgroundColor: "rgba(22,56,29,0.4)" }}>
              <p className="overline text-av-lime mb-2">Risposta breve</p>
              <p className="text-av-white text-lg leading-relaxed">{g.risposta_breve}</p>
            </div>

            {g.corpo && <p className="mt-8 text-av-white/85 text-lg leading-relaxed">{g.corpo}</p>}

            <Block title="Cause più frequenti" items={g.cause} icon={<span className="w-1.5 h-1.5 rounded-full bg-av-lime mt-2.5 shrink-0" />} />
            <Block title="Cosa puoi fare da solo" items={g.fai_da_te} icon={<Check size={18} className="text-av-lime mt-0.5 shrink-0" />} />
            <Block title="Errori da evitare" items={g.errori} icon={<X size={18} className="text-red-400 mt-0.5 shrink-0" />} />

            {g.quando_professionista && (
              <div className="mt-8">
                <h2 className="font-display text-2xl text-av-white mb-3">Quando rivolgersi a un professionista</h2>
                <p className="text-av-white/85 leading-relaxed">{g.quando_professionista}</p>
              </div>
            )}

            {g.sicurezza && (
              <div className="mt-6 flex items-start gap-3 rounded-xl border border-amber-500/40 bg-amber-500/10 p-4">
                <AlertTriangle size={20} className="text-amber-400 shrink-0 mt-0.5" />
                <p className="text-amber-200 text-sm">{g.sicurezza}</p>
              </div>
            )}

            {g.servizio && (
              <div className="mt-8 flex items-center gap-3 rounded-xl border border-white/10 p-4">
                <Wrench size={18} className="text-av-bright" />
                <span className="text-av-white/85 text-sm">Servizio correlato: <Link to="/servizi" className="text-av-lime underline">{g.servizio}</Link></span>
              </div>
            )}

            <p className="mt-8 text-xs text-av-gray/60">Pubblicato il {g.data_pubblicazione} · Aggiornato il {g.data_aggiornamento} · A cura di {g.autore || "Animaverde"}</p>

            <GuideCta />

            {related.length > 0 && (
              <div className="mt-12">
                <h2 className="font-display text-2xl text-av-white mb-5">Guide correlate</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {related.map((r) => <GuideCard key={r.id} g={r} />)}
                </div>
              </div>
            )}
          </Reveal>
        </div>
      </article>
    </>
  );
}
